import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Input, Button, Space, message, Tabs, Tag, Descriptions, Row, Col, DatePicker, InputNumber, Switch, Divider, Badge, Tooltip } from 'antd';
import { PlayCircleOutlined, ReloadOutlined, FormOutlined, CodeOutlined, SettingOutlined, RocketOutlined, BugOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { testApi } from '../services/api';
import { testTemplates, buildRequestBody } from '../utils/testTemplates';
import { getChannelLogo, buildChannelOptions } from '../utils/channelLogos';
import { useLanguage } from '../contexts/LanguageContext';
import SecurityNotice from '../components/SecurityNotice';
import { getEnvByHostname } from '../utils/env';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { TabPane } = Tabs;

export default function TestExecutor() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [envs, setEnvs] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [businessPartners, setBusinessPartners] = useState([]);
  const [result, setResult] = useState(null);
  const [useForm, setUseForm] = useState(true);
  const [selectedTestType, setSelectedTestType] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [formUserType, setFormUserType] = useState(null);

  useEffect(() => {
    loadChannels();
    // 恢复上次保存的表单数据
    const savedFormData = localStorage.getItem('testExecutorFormData');
    if (savedFormData) {
      try {
        const formData = JSON.parse(savedFormData);
        form.setFieldsValue(formData);
        setSelectedTestType(formData.testType);
        setSelectedChannel(formData.channelCode);
      } catch (e) {
        console.error('Failed to restore form data:', e);
      }
    } else {
      // 默认设置Business Partner
      form.setFieldsValue({ businessPartner: 'DEMO_ACCOUNT' });
    }
  }, []);

  const loadChannels = async () => {
    try {
      const { data } = await testApi.getChannels();
      const channelData = data.data || data;
      setChannels(channelData.channels || []);
      setEnvs(channelData.envs || []);
      setTestTypes(channelData.testTypes || []);
      setCurrencies(channelData.currencies || []);
      setBusinessPartners(channelData.businessPartners || []);
    } catch (error) {
      console.error('Failed to load channels:', error);
      message.error('加载配置失败');
    }
  };

  const handleExecute = async (values) => {
    console.log('handleExecute called with values:', values);
    console.log('Form all values:', form.getFieldsValue()); // 打印所有表单值
    
    // 检查登录状态
    const username = localStorage.getItem('username');
    const isLogin = localStorage.getItem('isLogin');
    if (!isLogin || !username) {
      message.error('请先登录');
      return;
    }
    
    // 获取所有表单值（包括右侧参数）
    const allValues = form.getFieldsValue();
    console.log('All form values:', allValues);
    
    // 保存表单数据到localStorage
    localStorage.setItem('testExecutorFormData', JSON.stringify(allValues));
    
    setLoading(true);
    try {
      let requestBody;
      if (useForm && selectedTestType && testTemplates[selectedTestType]) {
        requestBody = buildRequestBody(selectedTestType, allValues, allValues.channelCode, allValues.currency);
        console.log('Built request body:', requestBody);
      } else {
        requestBody = allValues.requestBody;
      }

      const payload = {
        ...allValues,
        requestBody: typeof requestBody === 'string' ? requestBody : JSON.stringify(requestBody, null, 2),
        executor: username,
      };
      
      console.log('Sending payload:', payload);
      const { data } = await testApi.executeTest(payload);
      console.log('Response:', data);
      
      setResult(data.data || data);
      message.success(t('executeSuccess'));
    } catch (error) {
      console.error('Execute error:', error);
      if (error.response?.status === 403) {
        message.error(error.response?.data?.msg || '权限不足，请联系管理员');
      } else {
        message.error(error.response?.data?.msg || error.message || t('executeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    const template = testTemplates[selectedTestType];
    if (!template) return null;

    // 获取字段配置（渠道特定或通用）
    let fields = template.fields;
    if (template.channelSpecific && template.channels) {
      const channelCode = selectedChannel || form.getFieldValue('channelCode');
      if (channelCode && template.channels[channelCode]) {
        fields = template.channels[channelCode];
      } else {
        return (
          <div style={{ 
            padding: 32, 
            textAlign: 'center', 
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: 8,
            border: '2px dashed #d1d5db'
          }}>
            <SettingOutlined style={{ fontSize: 24, marginBottom: 8 }} />
            <div>请先选择渠道以配置参数</div>
          </div>
        );
      }
    }

    if (!fields || fields.length === 0) return null;

    // 按属性分类字段
    const fieldGroups = {
      basic: { title: '基础信息', fields: [], icon: '📋' },
      name: { title: '姓名信息', fields: [], icon: '👤' },
      contact: { title: '联系方式', fields: [], icon: '📞' },
      identity: { title: '证件信息', fields: [], icon: '🪪' },
      address: { title: '地址信息', fields: [], icon: '📍' },
      bank: { title: '银行信息', fields: [], icon: '🏦' },
      other: { title: '其他信息', fields: [], icon: '⚙️' }
    };

    // 分类规则
    fields.forEach(field => {
      // showWhen 条件控制：字段值不匹配时跳过
      if (field.showWhen) {
        const watchedValue = field.showWhen.field === 'userType'
          ? (formUserType ?? form.getFieldValue(field.showWhen.field))
          : form.getFieldValue(field.showWhen.field);
        if (watchedValue !== undefined && watchedValue !== field.showWhen.value) return;
      }

      const name = field.name.toLowerCase();
      
      if (['externaluserid', 'usertype', 'paymentmethod', 'depositmethod', 'withdrawmethod'].includes(name)) {
        fieldGroups.basic.fields.push(field);
      } else if (name.includes('name') || name.includes('firstname') || name.includes('lastname') || name.includes('middlename') || name.includes('fullname')) {
        fieldGroups.name.fields.push(field);
      } else if (name.includes('phone') || name.includes('mobile') || name.includes('email')) {
        fieldGroups.contact.fields.push(field);
      } else if (name.includes('document') || name.includes('id') && !name.includes('userid') || name.includes('passport') || name.includes('nationality') || name.includes('dob') || name.includes('birth') || name.includes('bvn') || name.includes('nin')) {
        fieldGroups.identity.fields.push(field);
      } else if (name.includes('address') || name.includes('street') || name.includes('city') || name.includes('country') || name.includes('postal') || name.includes('residence') || name.includes('number') && field.label.includes('门牌')) {
        fieldGroups.address.fields.push(field);
      } else if (name.includes('bank') || name.includes('account') || name.includes('bic') || name.includes('swift')) {
        fieldGroups.bank.fields.push(field);
      } else {
        fieldGroups.other.fields.push(field);
      }
    });

    const renderField = (field) => {
      const defaultValue = field.default ? (typeof field.default === 'function' ? field.default() : field.default) : undefined;
      
      const label = (
        <Space size={4}>
          <span style={{ fontWeight: 500, fontSize: 13 }}>
            {field.label}
            {field.required && <span style={{ color: '#ff4d4f', marginLeft: 2 }}>*</span>}
          </span>
          {field.tip && (
            <Tooltip title={t(field.tip)} placement="topLeft">
              <span style={{ 
                cursor: 'help',
                color: '#1890ff',
                fontSize: 12,
                padding: '0 4px',
                background: '#e6f7ff',
                borderRadius: 3
              }}>
                ?
              </span>
            </Tooltip>
          )}
        </Space>
      );

      return (
        <Form.Item 
          key={field.name} 
          label={label} 
          name={field.name} 
          rules={[{ 
            required: field.required,
            message: `请${field.type === 'select' ? '选择' : '输入'}${field.label}`
          }]} 
          initialValue={defaultValue}
          style={{ marginBottom: 0 }}
        >
          {field.type === 'select' ? (
            <Select 
              placeholder={field.placeholder || `选择${field.label}`} 
              options={field.options.map(o => ({ label: o, value: o }))} 
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          ) : field.type === 'number' ? (
            <InputNumber 
              style={{ width: '100%' }} 
              placeholder={field.placeholder || `输入${field.label}`} 
              min={0}
            />
          ) : field.type === 'date' ? (
            <DatePicker 
              style={{ width: '100%' }} 
              format={field.format || 'YYYY-MM-DD'} 
              placeholder={field.placeholder || '选择日期'}
            />
          ) : field.type === 'textarea' ? (
            <Input.TextArea
              placeholder={field.placeholder || `输入${field.label}`} 
              rows={2}
            />
          ) : (
            <Input 
              placeholder={field.placeholder || `输入${field.label}`} 
            />
          )}
        </Form.Item>
      );
    };

    return (
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {Object.entries(fieldGroups).map(([key, group]) => {
          if (group.fields.length === 0) return null;
          
          return (
            <div key={key}>
              <div style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: '#374151',
                marginBottom: 16,
                paddingBottom: 8,
                borderBottom: '2px solid #e5e7eb'
              }}>
                {group.icon} {group.title}
              </div>
              <Row gutter={[16, 16]}>
                {group.fields.map(field => (
                  <Col xs={24} sm={12} md={8} lg={6} key={field.name}>
                    {renderField(field)}
                  </Col>
                ))}
              </Row>
            </div>
          );
        })}
      </Space>
    );
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      SUCCESS: { color: '#52c41a', icon: '✅', text: 'SUCCESS' },
      FAILED: { color: '#ff4d4f', icon: '❌', text: 'FAILED' },
      TIMEOUT: { color: '#faad14', icon: '⏰', text: 'TIMEOUT' }
    };
    const config = statusConfig[status] || statusConfig.FAILED;
    return (
      <Tag color={config.color} className="status-tag">
        {config.icon} {config.text}
      </Tag>
    );
  };

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* 顶部工具栏 */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 24px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <Space size={12}>
            <RocketOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>测试执行</span>
          </Space>
          <Space size={12}>
            <Switch 
              checkedChildren={<FormOutlined />} 
              unCheckedChildren={<CodeOutlined />} 
              checked={useForm}
              onChange={setUseForm}
            />
            <span style={{ color: '#fff', fontSize: 13 }}>
              {useForm ? t('formMode') : t('jsonMode')}
            </span>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadChannels}
              type="text"
              style={{ color: '#fff' }}
            >
              {t('refresh')}
            </Button>
          </Space>
        </div>

        {/* 主内容区 */}
        <Form form={form} layout="vertical" onFinish={handleExecute} onValuesChange={(changedValues) => {
          if ('userType' in changedValues) setFormUserType(changedValues.userType);
        }} onFinishFailed={(errorInfo) => {
          console.log('Form validation failed:', errorInfo);
          message.error('请填写所有必填字段');
        }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            {/* 左侧：基础配置 + 执行按钮 */}
            <div style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <SecurityNotice env={form.getFieldValue('env')} />
              
              <Card 
                className="modern-card"
                style={{ flex: 1, overflow: 'auto' }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>
                  ⚙️ 基础配置
                </div>
                <Form.Item label="渠道" name="channelCode" rules={[{ required: true }]}>
                  <Select 
                    placeholder="选择渠道" 
                    options={buildChannelOptions(channels)}
                    onChange={(value) => { 
                      setSelectedChannel(value); 
                      if (selectedTestType === 'ACCOUNT_CREATE') { 
                        form.resetFields(Object.keys(form.getFieldsValue()).filter(k => !['channelCode', 'env', 'testType', 'businessPartner', 'currency'].includes(k))); 
                      } 
                    }} 
                    showSearch 
                    filterOption={(input, option) =>
                      option.value.toLowerCase().includes(input.toLowerCase()) ||
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <Form.Item label="环境" name="env" rules={[{ required: true }]} initialValue={getEnvByHostname()}>
                  <Select options={envs.map(e => ({ label: e, value: e }))} />
                </Form.Item>
                <Form.Item label="测试类型" name="testType" rules={[{ required: true }]}>
                  <Select 
                    placeholder="选择类型" 
                    options={testTypes.map(t => ({ label: t, value: t }))} 
                    onChange={(value) => { 
                      setSelectedTestType(value); 
                      form.resetFields(Object.keys(form.getFieldsValue()).filter(k => !['channelCode', 'env', 'testType', 'businessPartner', 'currency'].includes(k))); 
                    }} 
                  />
                </Form.Item>
                <Form.Item label="Business Partner" name="businessPartner" rules={[{ required: true }]}>
                  <Select placeholder="选择Partner" options={businessPartners.map(b => ({ label: b, value: b }))} showSearch />
                </Form.Item>
                <Form.Item label="币种" name="currency" rules={[{ required: true }]}>
                  <Select placeholder="选择币种" options={currencies.map(c => ({ label: c, value: c }))} showSearch />
                </Form.Item>
              </Card>
              
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<PlayCircleOutlined />} 
                loading={loading} 
                size="large" 
                block
                className="primary-button"
                style={{ height: 48 }}
              >
                {loading ? '执行中...' : t('execute')}
              </Button>
            </div>

            {/* 右侧：开户参数 */}
            <Card 
            className="modern-card"
            style={{ flex: 1 }}
            bodyStyle={{ padding: '24px' }}
          >
            <div style={{ 
              fontSize: 16, 
              fontWeight: 600, 
              marginBottom: 20,
              paddingBottom: 12,
              borderBottom: '2px solid #667eea',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <BugOutlined style={{ color: '#667eea', fontSize: 18 }} />
              {useForm && selectedTestType && testTemplates[selectedTestType] 
                ? `${testTemplates[selectedTestType].label}参数` 
                : '请求参数'
              }
              {form.getFieldValue('channelCode') && selectedTestType === 'ACCOUNT_CREATE' && (() => {
                const channelCode = selectedChannel || form.getFieldValue('channelCode');
                const logo = getChannelLogo(channelCode);
                return (
                  <Tag color={logo.color} style={{ marginLeft: 8, fontSize: 13, padding: '4px 10px' }}>
                    <Space size={6}>
                      <span style={{ fontSize: 14 }}>{logo.emoji}</span>
                      <span>{logo.name}</span>
                    </Space>
                  </Tag>
                );
              })()}
            </div>
            
            <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: 8 }} key={`${selectedTestType}-${selectedChannel}`}>
              {useForm && selectedTestType && testTemplates[selectedTestType] ? (
                renderFormFields()
              ) : (
                <Form.Item label="请求体 (JSON)" name="requestBody">
                  <TextArea 
                    rows={20} 
                    placeholder='{\n  "amount": "100.00",\n  "currency": "BRL"\n}' 
                    style={{ fontFamily: 'Monaco, Consolas, monospace' }}
                  />
                </Form.Item>
              )}
            </div>
          </Card>
        </div>
      </Form>

      {/* 执行结果 */}
      {result && (
        <Card 
          className="modern-card result-section fade-in-up"
          style={{ marginTop: '16px' }}
          title={
            <Space>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{t('executionResult')}</span>
              {getStatusTag(result.status)}
            </Space>
          }
          extra={
            <Button 
              type="text" 
              danger
              onClick={() => setResult(null)}
              style={{ fontSize: 16 }}
            >
              × 关闭
            </Button>
          }
        >
          <Descriptions 
            column={{ xs: 1, sm: 2, md: 3, lg: 4 }} 
            bordered 
            size="middle"
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label={t('status')}>{getStatusTag(result.status)}</Descriptions.Item>
            <Descriptions.Item label={t('costTime')}>
              <Badge count={`${result.costMs}ms`} color={result.costMs > 5000 ? '#ff4d4f' : '#52c41a'} />
            </Descriptions.Item>
            <Descriptions.Item label={t('httpStatus')}>
              <Tag color={result.httpStatus === 200 ? 'success' : 'error'}>{result.httpStatus}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('traceId')}>
              <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                {result.traceId}
              </code>
            </Descriptions.Item>
            <Descriptions.Item label={t('channelOrderId')} span={2}>
              {result.channelOrderId ? (
                <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                  {result.channelOrderId}
                </code>
              ) : '-'}
            </Descriptions.Item>
            {result.errorMsg && (
              <Descriptions.Item label={t('errorMsg')} span={2}>
                <Tag color="error" style={{ maxWidth: '100%', wordBreak: 'break-all' }}>
                  {result.errorMsg}
                </Tag>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Tabs defaultActiveKey="response" size="large">
            <TabPane tab={`📤 ${t('response')}`} key="response">
              <div className="json-viewer">
                <ReactJson 
                  src={JSON.parse(result.responseBody || '{}')} 
                  collapsed={1}
                  theme="rjv-default"
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                />
              </div>
            </TabPane>
            <TabPane tab={`📥 ${t('request')}`} key="request">
              <div className="json-viewer">
                <ReactJson 
                  src={JSON.parse(result.requestBody || '{}')} 
                  collapsed={1}
                  theme="rjv-default"
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                />
              </div>
            </TabPane>
          </Tabs>
        </Card>
      )}
      </div>
    </div>
  );
}
